/**
 * `/meetings/:id` for somebody with an account.
 *
 * The page's whole job is to answer three questions the room does not ask for
 * itself — which meeting, what this account's role in it is, and whether it is
 * over — and then to get out of the way. The room is
 * `components/meeting/MeetingRoom`, and the guest's door to the same room is
 * `pages/Guest.tsx`.
 */

import { useT } from '../i18n';
import { useRouter } from '../router';
import { useStore } from '../lib/relay/store';
import { Empty } from '../components/Shell';
import { MeetingRoom } from '../components/meeting/MeetingRoom';
import type { Meeting, MeetingRole, Person } from '../lib/relay/types';

/**
 * What this account is in this meeting.
 *
 * The owner is the host; an invitee carries its own role; anybody else who got
 * this far is an attendee. `own` is the server's own answer to the first
 * question and is preferred to comparing ids, which is the same fact read less
 * reliably.
 */
export function roleIn(meeting: Meeting, me: Person | null): MeetingRole {
  if (meeting.own || (me && meeting.ownerId === me.id)) return 'host';
  const invited = me ? meeting.invitees.find((i) => i.userId === me.id) : undefined;
  return invited?.role ?? 'attendee';
}

export function MeetingRoomPage({ id }: { id: string }) {
  const t = useT();
  const { navigate } = useRouter();
  const meeting = useStore((s) => s.meetings().find((m) => m.id === id));
  const me = useStore((s) => s.me());

  if (!meeting) return <Empty title={t('app.notfound.title')} />;

  // Only the owner's own name is known here: the store holds people by id and
  // a meeting somebody else runs carries an id this account may never have met.
  const hostName = meeting.own ? me?.name : undefined;

  return (
    <MeetingRoom
      meetingId={meeting.id}
      title={meeting.title}
      role={roleIn(meeting, me)}
      policy={meeting.policy}
      {...(hostName ? { hostName } : {})}
      ended={meeting.status === 'ended' || meeting.status === 'cancelled'}
      onLeave={() => navigate('/meetings')}
    />
  );
}
